import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { t } from '../i18n'

type CardProps = {
  children: ReactNode
  onClick?: () => void
  to?: string
  params?: Record<string, string>
  active?: boolean
  dashed?: boolean
  quiet?: boolean
  className?: string
}

export function Card({
  children,
  onClick,
  to,
  params,
  active,
  dashed,
  quiet,
  className = '',
}: CardProps) {
  const tone = active
    ? 'bg-tint border-tint-strong'
    : dashed
      ? 'bg-glass-2 border-dashed border-accent/30'
      : quiet
        ? 'bg-glass-2 border-transparent'
        : 'glass-1 shadow-card'

  const classes = `block w-full rounded-card border p-4 text-left ${tone} ${className}`

  if (to) {
    return (
      <Link to={to} params={params} className={`pressable ${classes}`}>
        {children}
      </Link>
    )
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`pressable ${classes}`}>
        {children}
      </button>
    )
  }

  return <div className={classes}>{children}</div>
}

export function CardList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2">{children}</div>
}

type BadgeProps = {
  children: ReactNode
  colour?: string
  tint?: string
  strong?: boolean
}

export function Badge({ children, colour = 'var(--text-muted)', tint, strong }: BadgeProps) {
  return (
    <span
      className="tabular inline-flex shrink-0 items-center gap-[6px] rounded-chip px-2 py-[4px] text-note"
      style={
        strong
          ? { background: colour, color: 'var(--on-accent)' }
          : { background: tint ?? 'var(--fill-soft)', color: colour }
      }
    >
      {children}
    </span>
  )
}

export function Progress({ value }: { value: number }) {
  return (
    <div className="h-[3px] w-full overflow-hidden rounded-capsule bg-fill">
      <div
        className="h-full rounded-capsule bg-accent transition-[width] duration-row"
        style={{ width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%` }}
      />
    </div>
  )
}

export function ActiveDot({ colour, size = 7 }: { colour: string; size?: number }) {
  return (
    <span
      className="shrink-0 rounded-full"
      style={{ background: colour, width: `${size}px`, height: `${size}px` }}
      aria-hidden
    />
  )
}

type ChipProps = {
  label: string
  count?: number
  selected?: boolean
  disabled?: boolean
  colour?: string
  onClick: () => void
}

export function Chip({ label, count, selected, disabled, colour, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={colour ? { color: colour } : undefined}
      className={`pressable flex h-[34px] shrink-0 items-center gap-[7px] rounded-control border px-3 text-label uppercase leading-none transition-[background-color,border-color,color] duration-mark ${
        selected
          ? 'border-accent bg-tint text-accent-deep'
          : 'border-hairline bg-field text-text-strong'
      } ${disabled ? 'opacity-disabled' : ''}`}
    >
      {colour ? <ActiveDot colour={colour} size={7} /> : null}
      {label}
      {count === undefined ? null : (
        <span
          className={`tabular text-note font-normal tracking-normal normal-case ${
            selected ? 'text-accent-deep' : 'text-text-muted'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  )
}

type ButtonProps = {
  children: ReactNode
  onClick?: () => void
  tone?: 'primary' | 'secondary' | 'danger'
  compact?: boolean
  disabled?: boolean
  type?: 'button' | 'submit'
}

export function Button({
  children,
  onClick,
  tone = 'primary',
  compact,
  disabled,
  type = 'button',
}: ButtonProps) {
  const tones = {
    primary: 'bg-accent text-on-accent shadow-raised border-transparent',
    secondary: 'bg-glass-2 text-text border-hairline',
    danger: 'bg-danger/10 text-danger border-transparent',
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`pressable flex items-center justify-center gap-2 rounded-nested border font-medium leading-none disabled:opacity-disabled ${
        compact ? 'min-h-tap px-[14px] text-list' : 'min-h-field w-full px-4 text-row'
      } ${tones[tone]}`}
    >
      {children}
    </button>
  )
}

export function Empty({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="glass-2 rounded-card px-4 py-8 text-center">
      <div className="text-row">{title}</div>
      {hint ? <p className="mx-auto mt-2 max-w-[280px] text-hint text-text-muted">{hint}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

export function Loading() {
  return <div className="py-10 text-center text-hint text-text-muted">{t.common.loading}</div>
}

export function FatalError({ message }: { message: string }) {
  return (
    <div className="backdrop-blobs fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-bg px-6 text-center">
      <div>
        <div className="text-display">{t.common.failed}</div>
        <p className="mx-auto mt-2 max-w-[280px] text-body text-text-muted">{message}</p>
      </div>
      <div className="w-full max-w-[280px]">
        <Button onClick={() => window.location.reload()}>{t.common.reload}</Button>
      </div>
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="glass-2 rounded-card px-4 py-6 text-center">
      <div className="text-row">{t.common.failed}</div>
      <p className="mt-2 text-hint text-text-muted">{message}</p>
      {onRetry ? (
        <div className="mt-4">
          <Button tone="secondary" onClick={onRetry}>
            {t.common.retry}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
